// src/services/questionnaireDimensionRiskRules.ts
import { api } from "@/lib/apiClient";
import type { GroCrudScope } from "@/lib/groCrudScope";
import {
  createCompanyGroDimensionRiskRule,
  getCompanyGroTemplates,
  setCompanyGroDimensionRiskRuleStatus,
  updateCompanyGroDimensionRiskRule,
} from "@/services/modelImports";
import type {
  QuestionnaireDimensionRiskRule,
  QuestionnaireDimensionRiskRuleLookups,
  QuestionnaireDimensionRiskRuleUpsertIn,
} from "@/types/questionnaireDimensionRiskRules";
import { listDimensions } from "@/services/dimensions";

const isCompany = (scope: GroCrudScope) => scope === "company_model";

export async function listQuestionnaireDimensionRiskRules(
  params?: { dimension_id?: string; is_active?: boolean },
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) {
    const data = await getCompanyGroTemplates();
    return data.dimension_risk_rules.filter((item) => {
      if (params?.dimension_id && item.dimension_id !== params.dimension_id) return false;
      if (typeof params?.is_active === "boolean" && item.is_active !== params.is_active) return false;
      return true;
    }) as QuestionnaireDimensionRiskRule[];
  }

  return api.get<QuestionnaireDimensionRiskRule[]>("/dimension-risk-rules", {
    params: {
      dimension_id: params?.dimension_id || undefined,
      is_active: typeof params?.is_active === "boolean" ? params.is_active : undefined,
    },
  });
}

export async function getQuestionnaireDimensionRiskRule(
  ruleId: string,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) {
    const data = await getCompanyGroTemplates();
    const item = data.dimension_risk_rules.find((rule) => rule.id === ruleId);
    if (!item) throw new Error("Relacionamento modelo não encontrado.");
    return item as QuestionnaireDimensionRiskRule;
  }
  return api.get<QuestionnaireDimensionRiskRule>(`/dimension-risk-rules/${ruleId}`);
}

export async function listQuestionnaireDimensionRiskRuleLookups(
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) {
    const data = await getCompanyGroTemplates();
    return {
      dimensions: data.dimensions
        .filter((item) => item.is_active)
        .map((item) => ({ id: item.id, code: item.code, name: item.name, is_active: item.is_active })),
      risks: data.risks
        .filter((item) => item.is_active)
        .map((item) => ({ id: item.id, code: item.code, name: item.name, is_active: item.is_active })),
      probability_levels: data.probability_levels,
      severity_levels: data.severity_levels,
    } satisfies QuestionnaireDimensionRiskRuleLookups;
  }

  const [dimensions, lookups] = await Promise.all([
    listDimensions({ is_active: true }, scope),
    api.get<Omit<QuestionnaireDimensionRiskRuleLookups, "dimensions">>(
      "/dimension-risk-rules/lookups",
    ),
  ]);

  return {
    dimensions: dimensions.map((item) => ({
      id: item.id,
      code: item.code,
      name: item.name,
      is_active: item.is_active,
    })),
    risks: lookups.risks,
    probability_levels: lookups.probability_levels,
    severity_levels: lookups.severity_levels,
  } satisfies QuestionnaireDimensionRiskRuleLookups;
}

export async function createQuestionnaireDimensionRiskRule(
  payload: QuestionnaireDimensionRiskRuleUpsertIn,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) return createCompanyGroDimensionRiskRule(payload);
  return api.post<QuestionnaireDimensionRiskRule>("/dimension-risk-rules", payload);
}

export async function updateQuestionnaireDimensionRiskRule(
  ruleId: string,
  payload: QuestionnaireDimensionRiskRuleUpsertIn,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) return updateCompanyGroDimensionRiskRule(ruleId, payload);
  return api.put<QuestionnaireDimensionRiskRule>(`/dimension-risk-rules/${ruleId}`, payload);
}

export async function setQuestionnaireDimensionRiskRuleStatus(
  ruleId: string,
  is_active: boolean,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) return setCompanyGroDimensionRiskRuleStatus(ruleId, is_active);
  return api.patch<QuestionnaireDimensionRiskRule>(`/dimension-risk-rules/${ruleId}/status`, {
    is_active,
  });
}

export async function deleteQuestionnaireDimensionRiskRule(
  ruleId: string,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) {
    return api.delete<void>(`/company-gro-templates/dimension-risk-rules/${ruleId}`);
  }
  return api.delete<void>(`/dimension-risk-rules/${ruleId}`);
}
